import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  ArrowLeft, Plus, Send, FileText, Mail, Users,
  Search, Eye, CheckSquare, Folder, Filter, Download, X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RFQManager = () => {
  const navigate = useNavigate();
  const [rfqs, setRfqs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectFiles, setProjectFiles] = useState([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [supplierTypeFilter, setSupplierTypeFilter] = useState('All');
  const [selectedRfq, setSelectedRfq] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [formData, setFormData] = useState({
    subject: '',
    message: '',
    notes: ''
  });

  const supplierTypes = ['All', 'Materials', 'Tools', 'Equipment', 'Services', 'Other'];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Load RFQs
      const rfqsRes = await axios.get(`${API}/rfq`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRfqs(rfqsRes.data.rfqs || []);
      
      // Load projects
      const projectsRes = await axios.get(`${API}/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(projectsRes.data || []);
      
      // Load suppliers from CRM
      const suppliersRes = await axios.get(`${API}/crm/suppliers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuppliers(suppliersRes.data || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadProjectFiles = async (projectId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/rfq/project/${projectId}/files`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjectFiles(response.data.files || []);
    } catch (error) {
      console.error('Failed to load project files:', error);
      toast.error('Failed to load project files');
    }
  };

  const handleProjectSelect = (projectId) => {
    setSelectedProject(projectId);
    loadProjectFiles(projectId);
    
    // Auto-populate subject
    const project = projects.find(p => p.id === projectId);
    if (project) {
      setFormData({
        ...formData,
        subject: `RFQ: ${project.project_name || project.customer_name}`,
        message: `Dear Supplier,\n\nWe are requesting a quote for the following project:\n\nProject: ${project.project_name || project.customer_name}\nDescription: ${project.description || 'See attached files'}\n\nPlease review the attached documents and provide your best quote.\n\nThank you.`
      });
    }
  };

  const toggleSupplier = (supplier) => {
    const exists = selectedSuppliers.find(s => s.id === supplier.id);
    if (exists) {
      setSelectedSuppliers(selectedSuppliers.filter(s => s.id !== supplier.id));
    } else {
      setSelectedSuppliers([...selectedSuppliers, {
        id: supplier.id,
        name: supplier.company_name,
        email: supplier.contact_email
      }]);
    }
  };

  const toggleFile = (file) => {
    const exists = selectedFiles.find(f => f.id === file.id);
    if (exists) {
      setSelectedFiles(selectedFiles.filter(f => f.id !== file.id));
    } else {
      setSelectedFiles([...selectedFiles, file]);
    }
  };

  const handleFileUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    if (!selectedProject) {
      toast.error('Please select a project first');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append('file', file);
        formData.append('category', 'RFQ Documents');
        formData.append('notes', 'Uploaded for RFQ');

        const response = await axios.post(
          `${API}/projects/${selectedProject}/files`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data',
            },
          }
        );

        if (response.data) {
          setUploadedFiles(prev => [...prev, response.data]);
          setSelectedFiles(prev => [...prev, response.data]);
        }
      }
      
      toast.success(`${files.length} file(s) uploaded successfully`);
      // Reload project files to show newly uploaded files
      await loadProjectFiles(selectedProject);
    } catch (error) {
      console.error('Failed to upload files:', error);
      toast.error('Failed to upload files');
    } finally {
      setUploading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const handleSendRFQ = async () => {
    if (!selectedProject) {
      toast.error('Please select a project');
      return;
    }
    if (selectedSuppliers.length === 0) {
      toast.error('Please select at least one supplier');
      return;
    }
    if (!formData.subject || !formData.message) {
      toast.error('Please fill in subject and message');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/rfq`,
        {
          project_id: selectedProject,
          subject: formData.subject,
          message: formData.message,
          notes: formData.notes,
          suppliers: selectedSuppliers,
          attachments: selectedFiles.map(f => f.file_path)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`RFQ sent to ${selectedSuppliers.length} suppliers via BCC`);
      setShowCreateModal(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Failed to send RFQ:', error);
      toast.error('Failed to send RFQ');
    }
  };

  const resetForm = () => {
    setSelectedProject(null);
    setSelectedSuppliers([]);
    setSelectedFiles([]);
    setUploadedFiles([]);
    setProjectFiles([]);
    setSupplierTypeFilter('All');
    setFormData({ subject: '', message: '', notes: '' });
  };

  const handleViewRfq = async (rfq) => {
    setSelectedRfq(rfq);
    setShowDetailModal(true);
  };

  const handleDownloadPdf = async (rfqId, rfqNumber) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/rfq/${rfqId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });

      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${rfqNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);

      toast.success('RFQ PDF downloaded successfully!');
    } catch (error) {
      console.error('Failed to download PDF:', error);
      toast.error('Failed to download PDF');
    }
  };

  const filteredRfqs = rfqs.filter(rfq =>
    rfq.rfq_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    rfq.project_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredSuppliers = supplierTypeFilter === 'All'
    ? suppliers
    : suppliers.filter(s => s.supplier_type === supplierTypeFilter);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-4 mb-2">
            <Button
              onClick={() => navigate('/tools')}
              variant="outline"
              size="sm"
              className="font-semibold text-gray-900 border-gray-400"
            >
              <ArrowLeft size={16} className="mr-2" />
              Back to Tools
            </Button>
          </div>
          <h1 className="text-4xl font-black tracking-tight uppercase text-gray-900">RFQ Manager</h1>
          <p className="text-gray-700 mt-1 font-medium">Request for Quote - Send to Multiple Suppliers</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus size={16} className="mr-2" />
          New RFQ
        </Button>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-3 text-gray-500" />
            <Input
              placeholder="Search RFQs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 text-gray-900 border-gray-300"
            />
          </div>
        </CardContent>
      </Card>

      {/* RFQs List */}
      {loading ? (
        <Card>
          <CardContent className="py-12 text-center text-gray-700">
            Loading RFQs...
          </CardContent>
        </Card>
      ) : filteredRfqs.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Mail size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-900 mb-2 font-semibold text-lg">No RFQs found</p>
            <p className="text-sm text-gray-700 mb-4">
              Create an RFQ to request quotes from suppliers
            </p>
            <Button onClick={() => setShowCreateModal(true)}>
              <Plus size={16} className="mr-2" />
              Create Your First RFQ
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredRfqs.map((rfq) => (
            <Card key={rfq.id} className="hover:border-primary transition-all">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Send size={20} className="text-gray-600" />
                      <p className="text-xs font-mono text-gray-600 font-semibold">
                        {rfq.rfq_number}
                      </p>
                      <Badge variant="secondary">
                        {rfq.status.toUpperCase()}
                      </Badge>
                    </div>
                    <CardTitle className="text-base text-gray-900">{rfq.subject}</CardTitle>
                    <p className="text-sm text-gray-700 mt-1">
                      Project: {rfq.project_name}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleViewRfq(rfq)}
                    className="text-gray-900"
                  >
                    <Eye size={16} className="mr-1" />
                    View Details
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-700 font-medium">Sent to:</span>
                    <span className="text-gray-900">{rfq.suppliers.length} suppliers</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700 font-medium">Sent by:</span>
                    <span className="text-gray-900">{rfq.sent_by_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700 font-medium">Date:</span>
                    <span className="text-gray-900 font-mono text-xs">
                      {new Date(rfq.sent_at).toLocaleString()}
                    </span>
                  </div>
                  {rfq.attachments && rfq.attachments.length > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-700 font-medium">Attachments:</span>
                      <span className="text-gray-900">{rfq.attachments.length} files</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Stats */}
      <Card>
        <CardHeader>
          <CardTitle className="text-gray-900">RFQ Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 border border-gray-300 rounded-lg bg-gray-50">
              <p className="text-3xl font-bold text-gray-900">{rfqs.length}</p>
              <p className="text-xs text-gray-700 mt-1 font-semibold">Total RFQs</p>
            </div>
            <div className="text-center p-4 border border-gray-300 rounded-lg bg-green-50">
              <p className="text-3xl font-bold text-green-600">
                {rfqs.filter(r => r.status === 'sent').length}
              </p>
              <p className="text-xs text-gray-700 mt-1 font-semibold">Sent</p>
            </div>
            <div className="text-center p-4 border border-gray-300 rounded-lg bg-blue-50">
              <p className="text-3xl font-bold text-blue-600">
                {suppliers.length}
              </p>
              <p className="text-xs text-gray-700 mt-1 font-semibold">Suppliers</p>
            </div>
            <div className="text-center p-4 border border-gray-300 rounded-lg bg-orange-50">
              <p className="text-3xl font-bold text-orange-600">
                {rfqs.reduce((sum, r) => sum + r.responses_received, 0)}
              </p>
              <p className="text-xs text-gray-700 mt-1 font-semibold">Responses</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Create RFQ Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-900 text-xl">Create New RFQ</DialogTitle>
            <DialogDescription className="text-gray-700">
              Select project, files, and suppliers to send quote request
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            {/* Project Selection */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-gray-900">Project *</Label>
              <Select value={selectedProject || ''} onValueChange={handleProjectSelect}>
                <SelectTrigger className="border-gray-300 text-gray-900">
                  <SelectValue placeholder="Select a project..." />
                </SelectTrigger>
                <SelectContent>
                  {projects.map((project) => (
                    <SelectItem key={project.id} value={project.id}>
                      {project.project_name || project.customer_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Files Selection & Upload */}
            {selectedProject && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold text-gray-900">
                    Attach Files (Optional)
                  </Label>
                  <label htmlFor="rfq-file-upload">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={uploading}
                      onClick={() => document.getElementById('rfq-file-upload').click()}
                    >
                      <Plus size={14} className="mr-1" />
                      {uploading ? 'Uploading...' : 'Upload Files'}
                    </Button>
                  </label>
                  <input
                    id="rfq-file-upload"
                    type="file"
                    multiple
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                    accept="*/*"
                  />
                </div>
                <div className="border border-gray-300 rounded-lg p-3 max-h-48 overflow-y-auto">
                  {projectFiles.length === 0 ? (
                    <p className="text-sm text-gray-600 text-center py-4">
                      No files in project yet. Upload files above to attach them to this RFQ.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {projectFiles.map((file) => (
                        <div
                          key={file.id}
                          className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                          onClick={() => toggleFile(file)}
                        >
                          <input
                            type="checkbox"
                            checked={selectedFiles.some(f => f.id === file.id)}
                            onChange={() => toggleFile(file)}
                            className="w-4 h-4"
                          />
                          <FileText size={16} className="text-gray-600" />
                          <span className="text-sm text-gray-900">{file.filename}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-600">
                  {selectedFiles.length} file(s) selected to attach to RFQ
                </p>
              </div>
            )}

            {/* Suppliers Selection with Filter */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold text-gray-900">
                  Select Suppliers (BCC) *
                </Label>
                <div className="flex items-center gap-2">
                  <Filter size={14} className="text-gray-600" />
                  <Select value={supplierTypeFilter} onValueChange={setSupplierTypeFilter}>
                    <SelectTrigger className="w-[150px] h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {supplierTypes.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="border border-gray-300 rounded-lg p-3 max-h-48 overflow-y-auto">
                {suppliers.length === 0 ? (
                  <p className="text-sm text-gray-600 text-center py-4">
                    No suppliers in CRM. Add suppliers first.
                  </p>
                ) : filteredSuppliers.length === 0 ? (
                  <p className="text-sm text-gray-600 text-center py-4">
                    No suppliers found for type &ldquo;{supplierTypeFilter}&rdquo;
                  </p>
                ) : (
                  <div className="space-y-2">
                    {filteredSuppliers.map((supplier) => (
                      <div
                        key={supplier.id}
                        className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                        onClick={() => toggleSupplier(supplier)}
                      >
                        <input
                          type="checkbox"
                          checked={selectedSuppliers.some(s => s.id === supplier.id)}
                          onChange={() => toggleSupplier(supplier)}
                          className="w-4 h-4"
                        />
                        <Users size={16} className="text-gray-600" />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="text-sm text-gray-900 font-medium">{supplier.company_name}</p>
                            <Badge variant="outline" className="text-xs">
                              {supplier.supplier_type}
                            </Badge>
                          </div>
                          <p className="text-xs text-gray-600">{supplier.contact_email}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-600">
                {selectedSuppliers.length} supplier(s) selected (sent via BCC)
                {supplierTypeFilter !== 'All' && ` • Filtered by: ${supplierTypeFilter}`}
              </p>
            </div>

            {/* Subject */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-gray-900">Email Subject *</Label>
              <Input
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                placeholder="RFQ: Project Name"
                className="text-gray-900 border-gray-300"
              />
            </div>

            {/* Message */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-gray-900">Email Message *</Label>
              <Textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="Enter your message to suppliers..."
                rows={6}
                className="text-gray-900 border-gray-300"
              />
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-gray-900">Internal Notes (Optional)</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Internal notes (not sent to suppliers)..."
                rows={2}
                className="text-gray-900 border-gray-300"
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
              <p className="text-sm text-gray-800">
                <strong className="text-gray-900">Note:</strong> All suppliers will receive the RFQ via BCC (Blind Carbon Copy), so they won&apos;t see each other&apos;s email addresses.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateModal(false); resetForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleSendRFQ}>
              <Send size={16} className="mr-2" />
              Send RFQ
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* RFQ Detail Modal */}
      {showDetailModal && selectedRfq && (
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <div>
                  <DialogTitle className="text-gray-900 text-2xl font-black uppercase">
                    {selectedRfq.rfq_number}
                  </DialogTitle>
                  <DialogDescription className="text-gray-700">
                    Request for Quote Details
                  </DialogDescription>
                </div>
                <Badge variant="secondary" className="ml-4">
                  {selectedRfq.status.toUpperCase()}
                </Badge>
              </div>
            </DialogHeader>
            
            <div className="space-y-6">
              {/* Basic Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-semibold text-gray-900">RFQ Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-gray-600">RFQ Number</Label>
                      <p className="text-sm font-mono font-semibold text-gray-900">{selectedRfq.rfq_number}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-600">Date Sent</Label>
                      <p className="text-sm font-mono text-gray-900">
                        {new Date(selectedRfq.sent_at).toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-600">Project</Label>
                      <p className="text-sm font-semibold text-gray-900">{selectedRfq.project_name}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-600">Sent By</Label>
                      <p className="text-sm text-gray-900">{selectedRfq.sent_by_name}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Suppliers */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-semibold text-gray-900">
                    Suppliers ({selectedRfq.suppliers.length}) - Sent via BCC
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {selectedRfq.suppliers.map((supplier, index) => (
                      <div key={index} className="flex items-center gap-3 p-2 bg-gray-50 rounded">
                        <Users size={16} className="text-gray-600" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">{supplier.name}</p>
                          <p className="text-xs text-gray-600 font-mono">{supplier.email}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Subject & Message */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-semibold text-gray-900">Email Content</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-xs text-gray-600">Subject</Label>
                    <p className="text-sm font-semibold text-gray-900 mt-1">{selectedRfq.subject}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600">Message</Label>
                    <div className="mt-1 p-3 bg-gray-50 rounded border border-gray-200">
                      <p className="text-sm text-gray-900 whitespace-pre-wrap">{selectedRfq.message}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Attachments */}
              {selectedRfq.attachments && selectedRfq.attachments.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm font-semibold text-gray-900">
                      Attachments ({selectedRfq.attachments.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {selectedRfq.attachments.map((attachment, index) => (
                        <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                          <FileText size={16} className="text-gray-600" />
                          <span className="text-sm text-gray-900">{attachment.split('/').pop()}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Internal Notes */}
              {selectedRfq.notes && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm font-semibold text-gray-900">Internal Notes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-700 italic">{selectedRfq.notes}</p>
                  </CardContent>
                </Card>
              )}

              {/* Statistics */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-semibold text-gray-900">Response Statistics</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-3 bg-blue-50 rounded">
                      <p className="text-2xl font-bold text-blue-600">{selectedRfq.suppliers.length}</p>
                      <p className="text-xs text-gray-700 mt-1">Suppliers Contacted</p>
                    </div>
                    <div className="text-center p-3 bg-green-50 rounded">
                      <p className="text-2xl font-bold text-green-600">{selectedRfq.responses_received || 0}</p>
                      <p className="text-xs text-gray-700 mt-1">Responses Received</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <DialogFooter className="flex justify-between">
              <Button
                variant="outline"
                onClick={() => handleDownloadPdf(selectedRfq.id, selectedRfq.rfq_number)}
              >
                <Download size={16} className="mr-2" />
                Download PDF
              </Button>
              <Button variant="outline" onClick={() => setShowDetailModal(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default RFQManager;
